import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'

import { SparklesIcon } from '@heroicons/react/24/solid'
import { useQueryClient } from '@tanstack/react-query'
import { QUERY_KEYS } from 'constants/constants'
import Button from '../../../../components/tailwind/Button'
import { Heading } from '../../../../components/ui/Heading'
import { createAnalyseInput } from '../../../../constants/career-analyse'
import trackEvents from '../../../../constants/track-events'
import {
  useLanguageContext,
  usePartnerContext,
  useUserContext
} from '../../../../context'
import { mutation, query } from '../../../../graphql'
import { DIAGNOSTIC_VERSION } from '../../../../helpers'
import { trackEvent } from '../../../../helpers/analytics'
import { getCreateCareerInput } from '../../../../helpers/career'
import { mapChallengesToCoveredDimensions } from '../../../../helpers/diagnostic'
import { useCareersQuery } from '../../../../hooks/use-careers-query'
import { useSystemQuery } from '../../../../hooks/use-system-query'
import { useCareerPresetQuery } from '../../../../hooks/useCareerPresetQuery'
import { useMatchMeIfYouCanQuery } from '../../../../hooks/useMatchMeIfYouCanQuery'
import { MODES } from '../../../pricing/entitlements'
import { useLimitGuards } from '../../../pricing/guards'
import { invalidatePricingUsage, useEntitlements } from '../../../pricing/hooks'
import { LIMIT_DIALOG_ACTIONS } from '../../../pricing/pricing-dialog-context'
import { useDiscover } from '../../hooks/useDiscover'
import { useSelectionParams } from '../../hooks/useSelectionParams'
import {
  DEFAULT_CULTURAL_FIT_STATE,
  DEFAULT_DIMENSIONS,
  getOverallKey,
  getVariantFromScoreKey,
  getVariantStyles,
  MESSAGE_ROLES,
  MESSAGE_TYPES
} from '../../utils'

// SIMULATE CAREER DATA
const selectedChallenges = [
  {
    exam_id: 'TOWER_OF_LONDON'
  },
  {
    exam_id: 'FLANKER'
  },
  {
    exam_id: 'BIG_FIVE'
  },
  {
    exam_id: 'HEROS_JOURNEY'
  },
  {
    exam_id: 'RIASEC_IMAGES'
  },
  {
    exam_id: 'ANALOGICAL_REASONING'
  },
  {
    exam_id: 'EMOTIONS_GAME'
  }
]

export default function RelatedCareersSection({
  className,
  talentId,
  spaceId,
  isLoading
}) {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { language } = useLanguageContext()
  const {
    enableAssistant,
    setAssistantIsTyping,
    sendMessageToAssistant,
    setAssistantFocus
  } = useDiscover()
  const { cognitoUser } = useUserContext()
  const { data: system } = useSystemQuery({
    username: cognitoUser?.username
  })
  const { replaceCareer } = useSelectionParams()
  const queryClient = useQueryClient()

  const { mode } = useEntitlements()
  const { guardCareerActivation } = useLimitGuards()
  const [isCheckingLimit, setIsCheckingLimit] = useState(false)

  const { data, isLoading: isLoadingMatchMeIfYouCanQuery } =
    useMatchMeIfYouCanQuery({
      talentId,
      partnerId: partner.id
    })

  const { data: preset } = useCareerPresetQuery('17-1012.00')

  const presetName = preset?.name[language]

  const currentChallengeConfigId = system?.currentChallengeConfigId

  const { data: careers, refetch: refetchCareers } = useCareersQuery({
    select: (items) => {
      return items
        .filter(({ status }) => status === 'ACTIVE')
        .filter(({ id }) => !id.includes('PRODUCT_TOUR'))
        .filter(({ archived }) => !archived)
    }
  })

  const forceRefetchCareers = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.careers })
    refetchCareers({ cancelRefetch: false })
  }

  const hasOnlyOneCareer = careers.length === 1

  const onClickCareer = (careerId) => {
    trackEvent(trackEvents.DISCOVER_PRESSED_CAREER_RELATED_CAREERS)
    window.scrollTo({ top: 0, behavior: 'smooth' })

    replaceCareer(careerId)
  }

  const handleAssistantRelatedCareers = async () => {
    // Erst prüfen, ob ein Platz frei ist, sonst kündigt der Assistent eine
    // Stelle an, die nicht aktiv werden darf. Ist keiner frei, öffnet der
    // Guard den Limit-Dialog. Außerhalb von enforced gibt er sofort frei,
    // dort gibt es keinen Ladezustand.
    setIsCheckingLimit(mode === MODES.ENFORCED)
    const allowed = await guardCareerActivation(
      { partner_id: partner?.id, status: 'WAIT_ACTIVE', is_demo: true },
      {
        source: 'discover_related_careers',
        action: LIMIT_DIALOG_ACTIONS.DISCOVER
      }
    )
    setIsCheckingLimit(false)
    if (!allowed) return

    enableAssistant()

    setAssistantIsTyping(true)
    // Schritt 1: Einleitung
    sendMessageToAssistant({
      id: 1,
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.TEXT,
      title: t('discover.assistant.related_careers.intro.title'),
      description: t('discover.assistant.related_careers.intro.description')
    })
    // Schritt 2: "Ich erstelle dir noch eine weitere Stelle..."
    setTimeout(() => {
      sendMessageToAssistant({
        id: 2,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        title: t('discover.assistant.related_careers.create_career.title'),
        description: t(
          'discover.assistant.related_careers.create_career.description'
        )
      })
    }, 1200)

    createCareer()
  }

  /**
   * SEE Step2Configure for reference
   */
  const createCareer = async () => {
    // 1. Erstelle die Karriere mit Status WAIT_ACTIVE statt ACTIVE
    const input = {
      external_custom_id: undefined,
      diagnostic_version: DIAGNOSTIC_VERSION,
      challenge_config_id: currentChallengeConfigId,
      app_settings: {
        cultural_fit: JSON.stringify({ ...DEFAULT_CULTURAL_FIT_STATE }),
        challenges: JSON.stringify(
          selectedChallenges.map((c) => ({ exam_id: c.exam_id }))
        )
      },
      // Vorlagenstelle aus Discover, zählt als Beispieldaten. Das Top-Level-Flag
      // ist der gemeinsame Marker (helpers/sample-data.js), die Meta bleibt.
      is_demo: true,
      customer_dashboard_meta: JSON.stringify({
        is_demo: true,
        expert_mode_used: false
      }),
      ...getCreateCareerInput({
        title: presetName,
        category: '-1', // PRESET
        language,
        partner_id: partner?.id,
        status: 'WAIT_ACTIVE' // Änderung von ACTIVE zu WAIT_ACTIVE
      })
    }

    const new_career = await mutation({
      mutation: 'createCareer',
      input
    })

    // Berechne die Dimensionen aus den ausgewählten Challenges
    const selectedChallengesWithExamId = selectedChallenges.map(
      (challenge) => ({
        exam_id: challenge.exam_id
      })
    )

    // Hole die Dimensionen aus dem System
    // Verwende system.dimensions_pdf_reports.data statt system.dimensions
    const system_dimensions_data = JSON.parse(
      system.dimensions_pdf_reports.data
    )

    // Berechne die Dimensionen für die ausgewählten Challenges
    let dimensions = mapChallengesToCoveredDimensions({
      challenges: selectedChallengesWithExamId,
      dimensions: Object.values(system_dimensions_data)
    })

    // Wenn dimensions leer ist, verwende DEFAULT_DIMENSIONS
    if (!dimensions || dimensions.length === 0) {
      dimensions = DEFAULT_DIMENSIONS
    }

    // 2. Erstelle die Analyse aus der Vorlage
    const analyseInput = createAnalyseInput({
      career_id: new_career.id,
      status: 'COMPLETED',
      partner_id: partner?.id,
      username: cognitoUser?.username
    })

    // Explizit Berechtigungen setzen
    analyseInput.authorized_create = ['__create_' + partner?.id]
    analyseInput.authorized_read = ['__read_low_' + partner?.id]
    analyseInput.authorized_update = ['__update_' + partner?.id]
    analyseInput.authorized_delete = ['__delete_' + partner?.id]

    analyseInput.verified = true

    // Füge die berechneten Dimensionen zum Preset hinzu
    const presetData = preset?.career_analyse
      ? JSON.parse(preset.career_analyse)
      : {}

    // Throw error if presetData is empty object
    if (
      presetData &&
      typeof presetData === 'object' &&
      Object.keys(presetData).length === 0
    ) {
      throw new Error('Preset data is empty. Cannot proceed.')
    }

    // Stelle sicher, dass override_defaults existiert
    if (!presetData.override_defaults) {
      presetData.override_defaults = {}
    }

    // Füge die Dimensionen hinzu
    presetData.override_defaults.dimensions = dimensions
    presetData.override_defaults.selected_challenges =
      selectedChallengesWithExamId

    // Aktualisiere die Analyse-Eingabe
    analyseInput.input = JSON.stringify(presetData)

    // Füge Preset-Metadaten hinzu
    const presetCopy = { ...preset }
    delete presetCopy.career_analyse
    delete presetCopy.displayValue
    delete presetCopy.synonym
    delete presetCopy.syns

    // Stelle sicher, dass die name-Eigenschaft korrekt formatiert ist
    if (!presetCopy.name || typeof presetCopy.name !== 'object') {
      presetCopy.name = {
        de: preset?.name.de,
        en: preset?.name.en
      }
    }

    analyseInput.preset = JSON.stringify(presetCopy)

    await mutation({
      mutation: 'createCareerAnalyse',
      input: analyseInput
    })

    // Aktualisiere die Karriere mit den berechneten Dimensionen
    await mutation({
      mutation: 'updateCareer',
      input: {
        id: new_career.id,
        career_analyse: JSON.stringify({
          ...presetData,
          override_defaults: {
            ...presetData.override_defaults,
            dimensions,
            selected_challenges: selectedChallengesWithExamId,
            selection_difficulty: 'NORMAL'
          }
        })
      }
    })

    // Kurze Verzögerung, um sicherzustellen, dass die Daten gespeichert sind
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // 3. Rufe synthesizeAnalyze auf, um die Matching-Daten zu generieren
    await query({
      query: 'synthesizeAnalyze',
      variables: { career_id: new_career.id }
    })

    // Kurze Verzögerung, um sicherzustellen, dass die Synthese abgeschlossen ist
    await new Promise((resolve) => setTimeout(resolve, 1000))

    // 4. Aktiviere die Karriere nach der Synthese
    await mutation({
      mutation: 'updateCareer',
      input: {
        id: new_career.id,
        status: 'ACTIVE'
      }
    })

    // Die Stelle belegt jetzt einen Platz, Banner und Meter zählen neu
    invalidatePricingUsage({
      queryClient,
      partnerId: partner?.id,
      resource: 'careers'
    })

    setTimeout(() => {
      sendMessageToAssistant({
        id: 3,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT,
        title: t('discover.assistant.related_careers.compare_talent.title'),
        description: t(
          'discover.assistant.related_careers.compare_talent.description',
          {
            career_name: presetName
          }
        )
      })
      setAssistantIsTyping(false)
      setAssistantFocus(false)
      forceRefetchCareers()
    }, 2500)
  }

  return (
    <div className={clsx('', hasOnlyOneCareer && 'relative', className)}>
      <div className={clsx('', hasOnlyOneCareer && 'blur-sm')}>
        <div className='flex flex-row items-start justify-between'>
          <div className='w-full sm:w-9/12'>
            <Heading level={3}>
              {isLoading ? (
                <Skeleton width={200} />
              ) : (
                t('discover.related_careers_section.title')
              )}
            </Heading>
            {/* <p className='mt-4 text-base font-normal leading-[1.2] text-darkBlack'>
            {t('discover.related_careers_section.description')}
          </p> */}
          </div>
          {/* <AssistantButton
            onClick={() => showDemoAlert()}
            isLoading={isLoading}
          /> */}
        </div>
        {isLoadingMatchMeIfYouCanQuery ? (
          <div className='mt-6 rounded border border-lightGrey px-4 py-3'>
            <Skeleton count={3} />
          </div>
        ) : (
          <div className='mt-6 rounded border border-lightGrey'>
            <div className='flex flex-row justify-between bg-lightGrey'>
              <div className='px-4 py-3'>
                <Heading className={'uppercase'} level={6}>
                  {isLoading ? (
                    <Skeleton width={100} />
                  ) : (
                    t('discover.related_careers_section.active_careers')
                  )}
                </Heading>
              </div>
              <div className='w-24 px-4 py-3'>
                <Heading className={'uppercase'} level={6}>
                  {isLoading ? (
                    <Skeleton width={50} />
                  ) : (
                    t('discover.related_careers_section.score')
                  )}
                </Heading>
              </div>
            </div>
            {!isLoading &&
              !isLoadingMatchMeIfYouCanQuery &&
              data?.slice(0, 3).map((c, index, arr) => {
                const overall_key = getOverallKey(c)
                const variant = getVariantFromScoreKey(overall_key)
                return (
                  <div
                    key={`${c.title}-${c.matching_score}-${c.complete_dataset}-${index}`}
                    className={clsx(
                      'flex h-16 flex-row justify-between',
                      index < arr.length - 1 ? 'border-b border-lightGrey' : ''
                    )}
                  >
                    <button
                      onClick={() => onClickCareer(c.career_id)}
                      className='flex w-full flex-row items-center border-r border-lightGrey px-4'
                    >
                      <span className='text-sm font-medium text-buttonSecondaryBlue'>
                        {c.title}
                      </span>

                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        className='ml-0.5'
                      >
                        <path
                          fillRule='evenodd'
                          clipRule='evenodd'
                          d='M2 8C2 7.58579 2.33579 7.25 2.75 7.25L11.4393 7.25L8.21967 4.03033C7.92678 3.73744 7.92678 3.26256 8.21967 2.96967C8.51256 2.67678 8.98744 2.67678 9.28033 2.96967L13.7803 7.46967C14.0732 7.76256 14.0732 8.23744 13.7803 8.53033L9.28033 13.0303C8.98744 13.3232 8.51256 13.3232 8.21967 13.0303C7.92678 12.7374 7.92678 12.2626 8.21967 11.9697L11.4393 8.75L2.75 8.75C2.33579 8.75 2 8.41421 2 8Z'
                          fill='#1660F3'
                        />
                      </svg>
                    </button>
                    <div className='flex w-24 items-center justify-center px-6'>
                      <div
                        className={clsx(
                          'flex h-8 w-12 items-center justify-center rounded text-sm font-medium',
                          getVariantStyles(variant)
                        )}
                      >
                        {c.matching_score ? Math.round(c.matching_score) : '-'}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>
        )}
      </div>
      {hasOnlyOneCareer && (
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-6 px-6'>
          <div className='flex flex-row items-center gap-x-1'>
            <Heading level={3}>Passung zu anderen Stellen</Heading>
            <SparklesIcon className='size-6 text-brightBlue' />
          </div>
          <Button.PrimaryLG
            onClick={handleAssistantRelatedCareers}
            isLoading={isCheckingLimit}
            text='Klick mich'
          />
        </div>
      )}
    </div>
  )
}

RelatedCareersSection.propTypes = {
  className: PropTypes.string,
  talentId: PropTypes.string.isRequired,
  spaceId: PropTypes.string,
  isLoading: PropTypes.bool
}
